import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';

const DAY_OPTIONS = [[7, '7 days'], [14, '14 days'], [30, '30 days'], [0, 'All']];

function pct(v, digits = 2) {
  const n = Number(v);
  if (v == null || Number.isNaN(n)) return '—';
  return `${n > 0 ? '+' : ''}${n.toFixed(digits)}%`;
}

function money(v) {
  const n = Number(v);
  return v == null || Number.isNaN(n) ? '—' : `$${n.toFixed(2)}`;
}

/** "2026-10-01T10:42:13.5" -> "10/01 10:42" (already New York time from the API). */
function when(iso) {
  if (!iso) return '—';
  const [d, t = ''] = iso.split('T');
  return `${d.slice(5).replace('-', '/')} ${t.slice(0, 5)}`;
}

function typeLabel(r) {
  const base = { STOCK_BUY: 'BUY', STOCK_SELL: 'SHORT', OPTION_CALL: 'CALL', OPTION_PUT: 'PUT' }[r.signalType] || r.signalType || '—';
  return r.strike ? `${base} ${Number(r.strike)}` : base;
}

function ResultBadge({ r }) {
  const tone = r.result === 'WIN' ? 'bg-emerald-100 text-emerald-700'
    : r.result === 'LOSS' ? 'bg-rose-100 text-rose-700'
      : 'bg-slate-100 text-slate-500';
  const title = r.resultSource === 'GRADED'
    ? `Graded on the stock's move (${r.gradeNote || ''}) — best ${pct(r.maxFavorablePct)}, worst ${pct(r.maxAdversePct)}`
    : r.resultSource === 'ACTUAL' ? 'Realized result of the signal itself' : 'Not closed or graded yet';
  return (
    <span title={title} className={`inline-block rounded-md px-2 py-0.5 text-[11px] font-bold ${tone}`}>
      {r.result}{r.resultSource === 'GRADED' ? '*' : ''}
    </span>
  );
}

/**
 * Signal History — one plain row per signal any engine (or an admin) created, kept even
 * after the signal is deleted in review; only the ✕ here removes a row. Per-engine win %
 * on top, for tuning the engines from live results over 1-2+ weeks.
 */
export default function AdminSignalHistory() {
  const [days, setDays] = useState(14);
  const [engine, setEngine] = useState('');
  const [data, setData] = useState({ rows: [], summary: [], engines: [], total: {} });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    api.get('/admin/signal-history', { params: { days, engine: engine || undefined } })
      .then((r) => { setData(r.data || {}); setError(''); })
      .catch((e) => setError(e.response?.data?.message || 'Could not load signal history'))
      .finally(() => setLoading(false));
  }, [days, engine]);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setBusy(true);
    try { await api.post('/admin/signal-history/refresh'); load(); } catch (e) { setError('Refresh failed'); } finally { setBusy(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete ${row.symbol} (${row.engine}, ${when(row.createdAt)}) from Signal History? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/signal-history/${row.id}`);
      setData((d) => ({ ...d, rows: d.rows.filter((x) => x.id !== row.id) }));
    } catch (e) {
      alert(e.response?.data?.message || 'Delete failed');
    }
  };

  const rows = data.rows || [];
  const total = data.total || {};

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Signal History</h1>
          <p className="mt-1 text-sm text-slate-500">
            Every signal from every engine — stays here even if deleted in review, until you delete it on this page.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}
            className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm">
            {DAY_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select value={engine} onChange={(e) => setEngine(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm">
            <option value="">All engines</option>
            {(data.engines || []).map((e) => <option key={e} value={e}>{e}</option>)}
          </select>
          <button type="button" onClick={refresh} disabled={busy}
            className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50">
            {busy ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && <div className="mt-4 rounded-xl border border-rose-100 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700">{error}</div>}

      {/* Per-engine win % */}
      <div className="mt-5 bg-white rounded-xl border border-slate-100 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr>
              <th className="text-left p-3">Engine</th>
              <th className="text-right p-3">Signals</th>
              <th className="text-right p-3">Wins</th>
              <th className="text-right p-3">Losses</th>
              <th className="text-right p-3">Win %</th>
              <th className="text-right p-3">Avg closed P/L</th>
              <th className="text-right p-3">Avg graded move</th>
            </tr>
          </thead>
          <tbody>
            {(data.summary || []).map((s) => (
              <tr key={s.engine} className="border-t border-slate-50">
                <td className="p-3 font-medium">{s.engine}</td>
                <td className="p-3 text-right tabular-nums">{s.signals}</td>
                <td className="p-3 text-right tabular-nums text-emerald-700">{s.wins}</td>
                <td className="p-3 text-right tabular-nums text-rose-700">{s.losses}</td>
                <td className="p-3 text-right tabular-nums font-bold">{s.winPct != null ? `${s.winPct}%` : '—'}</td>
                <td className="p-3 text-right tabular-nums">{s.closedCount ? `${pct(s.avgClosedPct)} (${s.closedCount})` : '—'}</td>
                <td className="p-3 text-right tabular-nums">{s.gradedCount ? `${pct(s.avgGradedMovePct)} (${s.gradedCount})` : '—'}</td>
              </tr>
            ))}
            {(data.summary || []).length > 0 && (
              <tr className="border-t border-slate-200 bg-slate-50/60 font-semibold">
                <td className="p-3">All</td>
                <td className="p-3 text-right tabular-nums">{total.signals}</td>
                <td className="p-3 text-right tabular-nums text-emerald-700">{total.wins}</td>
                <td className="p-3 text-right tabular-nums text-rose-700">{total.losses}</td>
                <td className="p-3 text-right tabular-nums">{total.wins + total.losses > 0 ? `${total.winPct}%` : '—'}</td>
                <td className="p-3" colSpan={2} />
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-xs text-slate-400">
        WIN/LOSS = the signal&apos;s own realized P/L when it closed. WIN*/LOSS* = graded after the close on the
        stock&apos;s move in the signal&apos;s direction (alert → close; AI Signal: next open → close) — used for signals
        that were deleted, never published or never closed. Win % excludes open and ungraded rows.
      </p>

      {/* One plain row per signal */}
      <div className="mt-5 bg-white rounded-xl border border-slate-100 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr>
              <th className="text-left p-3">Date (ET)</th>
              <th className="text-left p-3">Engine</th>
              <th className="text-left p-3">Symbol</th>
              <th className="text-left p-3">Type</th>
              <th className="text-right p-3">Entry</th>
              <th className="text-right p-3">Exit</th>
              <th className="text-left p-3">Result</th>
              <th className="text-right p-3">%</th>
              <th className="text-left p-3">State</th>
              <th className="p-3" />
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && (
              <tr><td colSpan={10} className="p-6 text-center text-slate-400">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={10} className="p-6 text-center text-slate-400">No signals in this range yet</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-50">
                <td className="p-3 whitespace-nowrap tabular-nums text-slate-600">{when(r.createdAt)}</td>
                <td className="p-3 whitespace-nowrap">{r.engine}</td>
                <td className="p-3 font-bold">{r.symbol}</td>
                <td className="p-3 whitespace-nowrap">{typeLabel(r)}</td>
                <td className="p-3 text-right tabular-nums">{money(r.entry)}</td>
                <td className="p-3 text-right tabular-nums">{money(r.exit)}</td>
                <td className="p-3"><ResultBadge r={r} /></td>
                <td className={`p-3 text-right tabular-nums ${Number(r.resultPct) > 0 ? 'text-emerald-700' : Number(r.resultPct) < 0 ? 'text-rose-700' : 'text-slate-400'}`}>
                  {pct(r.resultPct)}
                </td>
                <td className="p-3 whitespace-nowrap text-xs text-slate-500">
                  {r.deleted ? 'Deleted' : r.published ? 'Published' : 'Draft'} · {r.status}
                </td>
                <td className="p-3 text-right">
                  <button type="button" onClick={() => remove(r)} title="Delete from Signal History"
                    className="rounded-md px-2 py-0.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600">✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
